<template>
  <div class="workflow-from-doc-page">
    <div class="header-section">
      <h2>🤖 AI Workflow Builder</h2>
      <p class="description">
        Transform any insurance requirements document into a comprehensive compliance workflow. 
        Our AI analyzes your document and automatically generates sophisticated checks, prompts, and validation rules.
      </p>
    </div>

    <!-- Analysis Section - MOVED TO TOP -->
    <div class="analysis-section primary-section">
      <div class="section-header">
        <h3>🔍 Document Analysis</h3>
        <p class="section-description">
          Provide your insurance requirements document and let AI build a comprehensive compliance workflow.
        </p>
      </div>

      <div class="input-container card">
      <div class="tab-buttons">
        <button :class="{ active: inputMode === 'paste' }" @click="inputMode = 'paste'">Paste Text</button>
        <button :class="{ active: inputMode === 'upload' }" @click="inputMode = 'upload'">Upload File</button>
      </div>

      <div v-if="inputMode === 'paste'" class="form-group">
        <textarea
          v-model="requirementsText"
          class="form-control"
          rows="15"
          placeholder="Paste the full text of your insurance requirements here."
        ></textarea>
      </div>

      <div v-if="inputMode === 'upload'" class="form-group">
        <input type="file" @change="handleFileUpload" class="form-control" accept=".pdf,.doc,.docx,.txt" />
        <p v-if="uploadedFile" class="file-info">Selected file: {{ uploadedFile.name }}</p>
      </div>

              <button @click="generateWorkflow" class="btn btn-primary btn-lg" :disabled="loading || !canGenerate">
          <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ loading ? 'Analyzing Document...' : '🚀 Analyze and Build Workflow' }}
        </button>
      </div>
    </div>
      
    <div v-if="error" class="error-message">
      <h3>❌ Analysis Error</h3>
      <p>We encountered an issue while analyzing your document. Please try again or contact support if the problem persists.</p>
      <pre>{{ error }}</pre>
    </div>

    <!-- Expandable Help Section - MOVED BELOW FUNCTIONALITY -->
    <div class="help-section">
      <!-- How to Build Your Workflow - Expandable -->
      <div class="expandable-card">
        <div class="card-header" @click="toggleHowToGuide">
          <h3>📋 How to Build Your Workflow</h3>
          <button class="expand-btn" :class="{ expanded: showHowToGuide }">
            <i :class="showHowToGuide ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
          </button>
        </div>
        <div v-show="showHowToGuide" class="expandable-content">
          <div class="steps-list">
            <div class="step">
              <span class="step-number">1</span>
              <div class="step-content">
                <strong>Provide Requirements Document</strong>
                <p>Paste text or upload your insurance requirements addendum, contract section, or compliance document</p>
              </div>
            </div>
            <div class="step">
              <span class="step-number">2</span>
              <div class="step-content">
                <strong>AI Analysis & Generation</strong>
                <p>Our AI analyzes the document and generates comprehensive compliance checks with detailed prompts</p>
              </div>
            </div>
            <div class="step">
              <span class="step-number">3</span>
              <div class="step-content">
                <strong>Review & Customize</strong>
                <p>Review generated checks, view enhanced prompts, and customize as needed before saving</p>
              </div>
            </div>
            <div class="step">
              <span class="step-number">4</span>
              <div class="step-content">
                <strong>Deploy & Use</strong>
                <p>Save as a workflow template or comprehensive check definition for immediate use</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- What is AI Workflow Generation - Expandable -->
      <div class="expandable-card">
        <div class="card-header" @click="toggleAboutSection">
          <h3>🚀 What is AI Workflow Generation?</h3>
          <button class="expand-btn" :class="{ expanded: showAboutSection }">
            <i :class="showAboutSection ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
          </button>
        </div>
        <div v-show="showAboutSection" class="expandable-content">
          <p>
            AI Workflow Generation automatically creates comprehensive compliance workflows from your documents by analyzing
            requirements and generating professional-grade automated checks with:
          </p>
          <div class="benefits-grid">
            <div class="benefit-item">
              <i class="fas fa-robot"></i>
              <strong>Smart Analysis</strong>
              <p>AI understands insurance terminology, requirements, and compliance patterns</p>
            </div>
            <div class="benefit-item">
              <i class="fas fa-cogs"></i>
              <strong>Complete Workflows</strong>
              <p>Generates full workflows with multiple checks, not just single requirements</p>
            </div>
            <div class="benefit-item">
              <i class="fas fa-magic"></i>
              <strong>Enhanced Prompts</strong>
              <p>Creates sophisticated, detailed prompts with structured analysis methodology</p>
            </div>
            <div class="benefit-item">
              <i class="fas fa-shield-alt"></i>
              <strong>Industry Expertise</strong>
              <p>Built-in knowledge of insurance compliance, coverage types, and industry standards</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Example Output - Expandable -->
      <div class="expandable-card">
        <div class="card-header" @click="toggleExampleSection">
          <h3>💡 What Gets Generated</h3>
          <button class="expand-btn" :class="{ expanded: showExampleSection }">
            <i :class="showExampleSection ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
          </button>
        </div>
        <div v-show="showExampleSection" class="expandable-content">
          <div class="example-comparison">
            <div class="input-example">
              <h4>📄 Your Input</h4>
              <div class="example-text input">
                "Contractor shall maintain General Liability insurance with minimum limits of $2,000,000 per occurrence. 
                Certificate holder shall be named as Additional Insured."
              </div>
            </div>
            <div class="output-example">
              <h4>🤖 AI Generated Checks</h4>
              <div class="generated-checks-preview">
                <div class="check-preview">
                  <strong>✓ General Liability Coverage Verification</strong>
                  <p>Sophisticated prompt to verify $2M minimum with evidence extraction</p>
                </div>
                <div class="check-preview">
                  <strong>✓ Additional Insured Status Validation</strong>
                  <p>Detailed analysis of certificate holder and additional insured language</p>
                </div>
                <div class="check-preview">
                  <strong>✓ Policy Validity and Compliance Check</strong>
                  <p>Verification of dates, carrier information, and policy status</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Section -->
    <div v-if="generatedChecks.length > 0" class="results-section">
      <div class="section-header">
        <h3>🎉 Generated Compliance Workflow</h3>
        <p class="section-description">
          Our AI has analyzed your document and created <strong>{{ generatedChecks.length }}</strong> sophisticated compliance checks. 
          Review the generated workflow below and customize as needed.
        </p>
      </div>

      <div class="results-container card">
        <div class="results-stats">
          <div class="stat-badge">
            <i class="fas fa-check-circle"></i>
            <span>{{ generatedChecks.length }} Checks Generated</span>
          </div>
          <div class="stat-badge">
            <i class="fas fa-brain"></i>
            <span>AI-Enhanced Prompts</span>
          </div>
          <div class="stat-badge">
            <i class="fas fa-shield-alt"></i>
            <span>Industry-Specific Logic</span>
          </div>
        </div>
      
      <div v-for="(check, index) in generatedChecks" :key="index" class="check-item">
        <strong>Name:</strong> {{ check.name }}<br>
        <strong>Severity:</strong> {{ check.suggested_severity }}<br>
        <strong>Description:</strong> {{ check.description }}<br>
        
        <!-- Enhanced: Show prompt quality indicators -->
        <div class="enhancement-indicators" v-if="check.prompt_template">
          <span class="enhancement-badge">
            📝 {{ check.prompt_template.length.toLocaleString() }} chars
          </span>
          <span class="enhancement-badge" v-if="check.check_type">
            🔧 {{ check.check_type }}
          </span>
          <button 
            class="view-prompt-btn" 
            @click="togglePromptView(index)"
            :class="{ active: showPrompt[index] }"
          >
            {{ showPrompt[index] ? 'Hide' : 'View' }} Enhanced Prompt
          </button>
        </div>
        
        <!-- Enhanced: Show full prompt template -->
        <div v-if="showPrompt[index]" class="prompt-template-view">
          <h5>🧠 AI Analysis Instructions:</h5>
          <pre class="prompt-content">{{ check.prompt_template }}</pre>
        </div>
      </div>

        <!-- Save Options -->
        <div class="save-options">
          <div class="save-header">
            <h4>💾 Save Your Workflow</h4>
            <p>Choose how to save your generated compliance workflow for future use.</p>
          </div>
          
          <div class="save-form">
            <div class="form-group">
              <label for="workflowName">Workflow Name</label>
              <input 
                id="workflowName"
                v-model="workflowName" 
                placeholder="Enter a descriptive name for your workflow"
                class="form-control"
              >
            </div>
            
            <div class="form-group">
              <label for="saveAsType">Save as</label>
              <select id="saveAsType" v-model="saveType" class="form-control">
                <option value="workflow">📋 Workflow Template (for document analysis)</option>
                <option value="definition">🎯 Comprehensive Check Definition (for project-based analysis)</option>
              </select>
              <div class="save-type-help">
                <div v-if="saveType === 'workflow'" class="help-text">
                  <i class="fas fa-info-circle"></i>
                  <strong>Workflow Template:</strong> Best for analyzing individual documents. Creates a reusable workflow that can be applied to any document.
                </div>
                <div v-if="saveType === 'definition'" class="help-text">
                  <i class="fas fa-info-circle"></i>
                  <strong>Check Definition:</strong> Best for project-based compliance. Creates a comprehensive check definition for ongoing project analysis.
                </div>
              </div>
            </div>
            
            <button 
              @click="save" 
              :disabled="isSaving || !workflowName" 
              class="btn btn-success btn-lg save-button"
            >
              <span v-if="isSaving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ isSaving ? 'Saving...' : '💾 Create ' + (saveType === 'workflow' ? 'Workflow' : 'Check Definition') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import api from '@/services/api';

export default {
  name: 'WorkflowFromDocPage',
  setup() {
    const inputMode = ref('paste'); // 'paste' or 'upload'
    const requirementsText = ref('');
    const uploadedFile = ref(null);
    const loading = ref(false);
    const isSaving = ref(false);
    const error = ref(null);
    const generatedChecks = ref([]);
    const workflowName = ref('');
    const saveType = ref('workflow'); // 'workflow' or 'definition'
    const showPrompt = ref({}); // Track which prompts are expanded
    
    // Expandable sections state
    const showHowToGuide = ref(false);
    const showAboutSection = ref(false);
    const showExampleSection = ref(false);

    const canGenerate = computed(() => {
      return (inputMode.value === 'paste' && requirementsText.value.trim() !== '') || (inputMode.value === 'upload' && uploadedFile.value !== null);
    });

    const handleFileUpload = (event) => {
      uploadedFile.value = event.target.files[0];
    };

    const generateWorkflow = async () => {
      // Prevent multiple simultaneous requests
      if (loading.value) {
        console.log('Already generating workflow, ignoring request');
        return;
      }
      
      loading.value = true;
      error.value = null;
      generatedChecks.value = [];
      
      console.log('🚀 Starting workflow generation...');

      try {
        let payload;
        if (inputMode.value === 'paste') {
          // Enhanced payload with context for sophisticated analysis
          payload = { 
            requirements_text: requirementsText.value,
            document_type: 'insurance_requirements', // Default type
            industry: 'general', // Default industry
            entity_names: [] // Will be auto-extracted by the enhanced system
          };
        } else {
          // For file uploads, we'd use FormData, which is more complex.
          // For this MVP, we will focus on the pasted text path.
          // A full implementation would handle reading the file content here.
          alert("File upload is not implemented in this prototype. Please use the paste text option.");
          loading.value = false;
          return;
        }

        console.log('📡 Sending request to contract-requirement-extractor...');
        const response = await api.post('/contract-requirement-extractor/', payload, {
          timeout: 360000 // 6 minutes timeout for contract analysis
        });
        
        console.log('✅ Received response:', response.data);
        console.log('Number of checks generated:', response.data?.length || 0);
        
        if (response.data && response.data.length > 0) {
          generatedChecks.value = response.data;
          workflowName.value = "Enhanced AI Analysis - " + new Date().toLocaleDateString();
          console.log('✅ Successfully set generated checks:', generatedChecks.value.length);
        } else {
          console.warn('⚠️ No checks returned from API');
          error.value = 'No compliance checks were generated. Please try with different requirements text.';
        }
        
        // Scroll to results section
        setTimeout(() => {
          const resultsSection = document.querySelector('.results-section');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);

      } catch (e) {
        console.error("Workflow generation failed:", e);
        console.error("Full error object:", e);
        console.error("Response data:", e.response?.data);
        console.error("Response status:", e.response?.status);
        
        if (e.response?.status === 504) {
          error.value = "The AI analysis is taking longer than expected. This may be due to a complex document. Please try again or contact support if the issue persists.";
        } else {
          error.value = e.response ? e.response.data : e.message;
        }
      } finally {
        loading.value = false;
      }
    };

    const togglePromptView = (index) => {
      showPrompt.value[index] = !showPrompt.value[index];
    };

    const save = async () => {
      if (!workflowName.value || generatedChecks.value.length === 0) {
        alert("Please provide a name and generate checks before saving.");
        return;
      }

      isSaving.value = true;
      error.value = null;

      try {
        const payload = {
          name: workflowName.value,
          description: `AI-generated ${saveType.value}`,
          checks: generatedChecks.value
        };

        const endpoint = saveType.value === 'workflow'
          ? '/workflow-assembly/'
          : '/comprehensive-check-assembly/';

        await api.post(endpoint, payload);
        
        alert(`${saveType.value === 'workflow' ? 'Workflow' : 'Check Definition'} saved successfully!`);
        generatedChecks.value = [];
        workflowName.value = '';
        showPrompt.value = {}; // Reset prompt views

      } catch (e) {
        console.error("Save failed:", e);
        error.value = e.response ? e.response.data : e.message;
      } finally {
        isSaving.value = false;
      }
    };

    // Toggle functions for expandable sections
    const toggleHowToGuide = () => {
      showHowToGuide.value = !showHowToGuide.value;
    };

    const toggleAboutSection = () => {
      showAboutSection.value = !showAboutSection.value;
    };

    const toggleExampleSection = () => {
      showExampleSection.value = !showExampleSection.value;
    };

    return {
      inputMode,
      requirementsText,
      uploadedFile,
      loading,
      isSaving,
      error,
      generatedChecks,
      workflowName,
      saveType,
      showPrompt,
      showHowToGuide,
      showAboutSection,
      showExampleSection,
      canGenerate,
      handleFileUpload,
      generateWorkflow,
      togglePromptView,
      toggleHowToGuide,
      toggleAboutSection,
      toggleExampleSection,
      save,
    };
  },
};
</script>

<style scoped>
.workflow-from-doc-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Header section styling */
.header-section {
  margin-bottom: 30px;
  text-align: center;
}

.header-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 2.2rem;
}

.description {
  color: #7f8c8d;
  font-size: 16px;
  max-width: 800px;
  margin: 0 auto;
}

/* Legacy styles removed - content now in expandable cards */

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.benefit-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.benefit-item i {
  font-size: 2em;
  color: #3498db;
  margin-bottom: 10px;
}

.benefit-item strong {
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 14px;
}

.benefit-item p {
  margin: 0;
  color: #7f8c8d;
  font-size: 13px;
  line-height: 1.4;
}

/* Steps list styling */
.steps-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.step-number {
  width: 30px;
  height: 30px;
  background: #3498db;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
}

.step-content strong {
  color: #2c3e50;
  margin-bottom: 5px;
  display: block;
}

.step-content p {
  margin: 0;
  color: #7f8c8d;
  line-height: 1.4;
}

/* Example comparison */
.example-comparison {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 20px;
}

.input-example, .output-example {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.example-text {
  background: white;
  padding: 15px;
  border-radius: 6px;
  border-left: 4px solid #e74c3c;
  font-style: italic;
  margin-top: 10px;
}

.generated-checks-preview {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.check-preview {
  background: white;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #27ae60;
}

.check-preview strong {
  color: #27ae60;
  display: block;
  margin-bottom: 5px;
}

.check-preview p {
  margin: 0;
  color: #7f8c8d;
  font-size: 13px;
}

/* Analysis Section - PRIMARY FUNCTIONALITY */
.analysis-section {
  margin-bottom: 30px;
}

.primary-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 40px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
}

.primary-section .section-header {
  color: white;
  margin-bottom: 25px;
}

.primary-section .section-header h3 {
  color: white;
  font-size: 1.8rem;
  margin-bottom: 8px;
}

.primary-section .section-description {
  color: rgba(255,255,255,0.9);
  font-size: 16px;
}

.section-header {
  text-align: center;
  margin-bottom: 20px;
}

.section-header h3 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.section-description {
  color: #7f8c8d;
  font-size: 16px;
  max-width: 600px;
  margin: 0 auto;
}

/* Input container */
.input-container {
  background: rgba(255,255,255,0.95);
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
}

/* Help Section - Expandable Cards */
.help-section {
  margin-top: 40px;
  margin-bottom: 30px;
}

.expandable-card {
  background: white;
  border-radius: 12px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e9ecef;
  overflow: hidden;
  transition: all 0.3s ease;
}

.expandable-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  border-color: #3498db;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  cursor: pointer;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.card-header:hover {
  background: #e9ecef;
}

.card-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
  flex-grow: 1;
}

.expand-btn {
  background: none;
  border: none;
  color: #3498db;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s;
  margin-left: 15px;
}

.expand-btn:hover {
  background: rgba(52, 152, 219, 0.1);
  color: #2980b9;
}

.expand-btn.expanded {
  color: #e74c3c;
}

.expandable-content {
  padding: 25px;
  border-top: 1px solid #f1f3f4;
  background: white;
}

.tab-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.tab-buttons button {
  padding: 10px 20px;
  border: 1px solid #ddd;
  background-color: #f8f9fa;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.tab-buttons button:hover {
  background-color: #e9ecef;
}

.tab-buttons button.active {
  background-color: #3498db;
  color: white;
  border-color: #3498db;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
}

.form-control {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

/* Results Section */
.results-section {
  margin-bottom: 30px;
}

.results-container {
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid #27ae60;
}

.results-stats {
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  flex-wrap: wrap;
}

.stat-badge {
  background: #e8f8f5;
  color: #27ae60;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-badge i {
  font-size: 16px;
}

/* Check items */
.check-item {
  border-bottom: 1px solid #eee;
  padding: 20px 0;
  margin-bottom: 15px;
}

.check-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

/* Enhancement indicators */
.enhancement-indicators {
  margin-top: 15px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.enhancement-badge {
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: bold;
}

.view-prompt-btn {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 15px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: background-color 0.2s;
}

.view-prompt-btn:hover {
  background-color: #45a049;
}

.view-prompt-btn.active {
  background-color: #f44336;
}

.prompt-template-view {
  margin-top: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.prompt-template-view h5 {
  background-color: #f8f9fa;
  margin: 0;
  padding: 12px 20px;
  border-bottom: 1px solid #ddd;
  font-size: 14px;
  font-weight: 600;
}

.prompt-content {
  margin: 0;
  padding: 20px;
  background-color: #ffffff;
  font-size: 13px;
  line-height: 1.6;
  max-height: 400px;
  overflow-y: auto;
  color: #444;
  font-family: 'Courier New', monospace;
}

/* Save Options */
.save-options {
  margin-top: 30px;
  padding-top: 25px;
  border-top: 2px solid #ecf0f1;
}

.save-header {
  text-align: center;
  margin-bottom: 20px;
}

.save-header h4 {
  color: #2c3e50;
  margin-bottom: 8px;
}

.save-header p {
  color: #7f8c8d;
  margin: 0;
}

.save-form {
  max-width: 500px;
  margin: 0 auto;
}

.save-type-help {
  margin-top: 10px;
}

.help-text {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
  color: #6c757d;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.help-text i {
  color: #3498db;
  margin-top: 2px;
}

.save-button {
  width: 100%;
  margin-top: 20px;
  padding: 15px;
  font-size: 16px;
  font-weight: 600;
}

/* Error message */
.error-message {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-left: 4px solid #e53e3e;
  border-radius: 6px;
  padding: 20px;
  margin: 20px 0;
}

.error-message h3 {
  color: #c53030;
  margin-bottom: 10px;
}

.error-message p {
  color: #742a2a;
  margin-bottom: 15px;
}

.error-message pre {
  background: #fed7d7;
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  color: #742a2a;
}

/* Button styling */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
}

.btn-success {
  background: #27ae60;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #229954;
}

.btn-lg {
  padding: 15px 30px;
  font-size: 16px;
}

/* Responsive */
@media (max-width: 768px) {
  .workflow-from-doc-page {
    padding: 15px;
  }
  
  .primary-section {
    padding: 20px;
    margin-bottom: 30px;
  }
  
  .primary-section .section-header h3 {
    font-size: 1.5rem;
  }
  
  .card-header {
    padding: 15px 20px;
  }
  
  .card-header h3 {
    font-size: 1rem;
  }
  
  .expandable-content {
    padding: 20px;
  }
  
  .example-comparison {
    grid-template-columns: 1fr;
  }
  
  .benefits-grid {
    grid-template-columns: 1fr;
  }
  
  .results-stats {
    flex-direction: column;
  }
}
</style> 